"use client";

import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { CcTrafficSelect } from "@/components/services/seo/CcTrafficSelect";
import { CcTrafficSliderField } from "@/components/services/seo/CcTrafficSliderField";
import {
  calculatorCurrency,
  type TrafficCalculatorSliderRange,
} from "@/components/services/seo/seo-revenue-projection";
import { CcCalculatorCollapsible } from "@/components/services/shared/calculator/CcCalculatorCollapsible";
import { CcSliderResultsCalculatorLayout } from "@/components/services/shared/calculator/CcSliderResultsCalculatorLayout";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AiAutomationHubspotSubpageRoiCalculatorCopy } from "./ai-automation-page-copy";
import { CcHubSpotRoiResultsPanel } from "./CcHubSpotRoiResultsPanel";
import {
  getHubSpotRoiIndustryPreset,
  HUBSPOT_ROI_INDUSTRY_PRESETS,
  HUBSPOT_ROI_SLIDER_RANGES,
  projectHubSpotRoi,
  type HubSpotRoiField,
  type HubSpotRoiIndustryId,
} from "./hubspot-roi-projection";

function defaultsFromPreset(id: HubSpotRoiIndustryId) {
  const preset = getHubSpotRoiIndustryPreset(id);
  return {
    employees: String(preset.defaults.employees),
    hoursPerWeek: String(preset.defaults.hoursPerWeek),
    hourlyCost: String(preset.defaults.hourlyCost),
    errorRate: String(preset.defaults.errorRate),
    automationEfficiency: preset.defaults.automationEfficiency,
  };
}

function rawForShare(raw: string) {
  return raw.replace(/\s/g, "");
}

type CcHubSpotRoiCalculatorProps = {
  copy: AiAutomationHubspotSubpageRoiCalculatorCopy;
};

export function CcHubSpotRoiCalculator({ copy }: CcHubSpotRoiCalculatorProps) {
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const locale = copy.locale;
  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: calculatorCurrency(locale),
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  const initial = defaultsFromPreset("custom");
  const [industry, setIndustry] = useState<HubSpotRoiIndustryId>("custom");
  const [automationEfficiency, setAutomationEfficiency] = useState(
    initial.automationEfficiency,
  );
  const [employees, setEmployees] = useState(initial.employees);
  const [hoursPerWeek, setHoursPerWeek] = useState(initial.hoursPerWeek);
  const [hourlyCost, setHourlyCost] = useState(initial.hourlyCost);
  const [errorRate, setErrorRate] = useState(initial.errorRate);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareBase, setShareBase] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("e");
    const h = params.get("h");
    const c = params.get("c");
    const r = params.get("r");
    const i = params.get("i") as HubSpotRoiIndustryId | null;

    if (i && HUBSPOT_ROI_INDUSTRY_PRESETS.some((preset) => preset.value === i)) {
      const preset = getHubSpotRoiIndustryPreset(i);
      setIndustry(i);
      setAutomationEfficiency(preset.defaults.automationEfficiency);
      setEmployees(e ?? String(preset.defaults.employees));
      setHoursPerWeek(h ?? String(preset.defaults.hoursPerWeek));
      setHourlyCost(c ?? String(preset.defaults.hourlyCost));
      setErrorRate(r ?? String(preset.defaults.errorRate));
    } else if (e || h || c || r) {
      if (e) setEmployees(e);
      if (h) setHoursPerWeek(h);
      if (c) setHourlyCost(c);
      if (r) setErrorRate(r);
    }

    setShareBase(`${window.location.origin}${window.location.pathname}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once from shareable URL
  }, []);

  const onIndustryChange = useCallback((value: string) => {
    const id = value as HubSpotRoiIndustryId;
    const next = defaultsFromPreset(id);
    setIndustry(id);
    setAutomationEfficiency(next.automationEfficiency);
    setEmployees(next.employees);
    setHoursPerWeek(next.hoursPerWeek);
    setHourlyCost(next.hourlyCost);
    setErrorRate(next.errorRate);
  }, []);

  const markCustom = useCallback(() => {
    setIndustry("custom");
  }, []);

  const setEmployeesCustom = useCallback(
    (next: string) => {
      markCustom();
      setEmployees(next);
    },
    [markCustom],
  );
  const setHoursCustom = useCallback(
    (next: string) => {
      markCustom();
      setHoursPerWeek(next);
    },
    [markCustom],
  );
  const setHourlyCostCustom = useCallback(
    (next: string) => {
      markCustom();
      setHourlyCost(next);
    },
    [markCustom],
  );
  const setErrorRateCustom = useCallback(
    (next: string) => {
      markCustom();
      setErrorRate(next);
    },
    [markCustom],
  );

  const result = projectHubSpotRoi({
    employeesRaw: employees,
    hoursPerWeekRaw: hoursPerWeek,
    hourlyCostRaw: hourlyCost,
    errorRateRaw: errorRate,
    automationEfficiency,
  });

  const problem = useCallback(
    (field: HubSpotRoiField) => {
      if (result.kind !== "incomplete") return null;
      if (result.invalid.includes(field)) {
        return field === "errorRate" ? copy.percentInvalid : copy.wholeNumberInvalid;
      }
      if (result.empty.includes(field)) {
        return copy.missing[field];
      }
      return null;
    },
    [copy.missing, copy.percentInvalid, copy.wholeNumberInvalid, result],
  );

  const industryOptions = useMemo(
    () =>
      HUBSPOT_ROI_INDUSTRY_PRESETS.map((preset) => ({
        value: preset.value,
        label: copy.industryLabels[preset.value],
      })),
    [copy.industryLabels],
  );

  const shareUrl = useMemo(() => {
    if (!shareBase) return null;
    const params = new URLSearchParams();
    params.set("e", rawForShare(employees));
    params.set("h", rawForShare(hoursPerWeek));
    params.set("c", rawForShare(hourlyCost));
    params.set("r", rawForShare(errorRate));
    if (industry !== "custom") {
      params.set("i", industry);
    }
    return `${shareBase}?${params.toString()}`;
  }, [shareBase, employees, hoursPerWeek, hourlyCost, errorRate, industry]);

  const onCopyShareLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    } catch {
      setShareCopied(false);
    }
  }, [shareUrl]);

  const sliderField = (
    field: HubSpotRoiField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    options: {
      kind: "whole" | "percent";
      range: TrafficCalculatorSliderRange;
      unit?: string;
    },
  ) => (
    <CcTrafficSliderField
      id={`${ids}-${field}`}
      field={field}
      label={label}
      raw={raw}
      onChange={onChange}
      kind={options.kind}
      range={options.range}
      locale={locale}
      unit={options.unit}
      error={problem(field)}
    />
  );

  return (
    <section
      className="cc-section cc-hubspot-roi-calculator"
      aria-labelledby="cc-hubspot-roi-calculator-title"
      data-cc-nav="light"
      {...devSectionAttr("ai-automation-hubspot-roi")}
    >
      <div className="cc-container">
        <header className="cc-seo-value__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.sectionEyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-hubspot-roi-calculator-title" className="cc-seo-heading">
            {copy.sectionTitle}
          </h2>
          <p className="cc-seo-lead cc-seo-value__lead">{copy.sectionLead}</p>
        </header>

        <div className="cc-traffic" data-traffic-calculator-version="2">
          <CcSliderResultsCalculatorLayout
            showHeader={false}
            controls={
              <>
                <p className="cc-traffic__text cc-hubspot-roi-calculator__intro">{copy.text}</p>
                <div className="cc-traffic__field">
                  <label className="cc-traffic__label" htmlFor={`${ids}-industry`}>
                    {copy.industryPresetLabel}
                  </label>
                  <CcTrafficSelect
                    id={`${ids}-industry`}
                    value={industry}
                    onValueChange={onIndustryChange}
                    options={industryOptions}
                  />
                </div>
                {sliderField(
                  "employees",
                  copy.employeesLabel,
                  employees,
                  setEmployeesCustom,
                  {
                    kind: "whole",
                    range: HUBSPOT_ROI_SLIDER_RANGES.employees,
                  },
                )}
                {sliderField(
                  "hoursPerWeek",
                  copy.hoursPerWeekLabel,
                  hoursPerWeek,
                  setHoursCustom,
                  {
                    kind: "whole",
                    range: HUBSPOT_ROI_SLIDER_RANGES.hoursPerWeek,
                    unit: copy.hoursUnitShort,
                  },
                )}
                {sliderField(
                  "hourlyCost",
                  copy.hourlyCostLabel,
                  hourlyCost,
                  setHourlyCostCustom,
                  {
                    kind: "whole",
                    range: HUBSPOT_ROI_SLIDER_RANGES.hourlyCost,
                    unit: copy.valueUnit,
                  },
                )}
                {sliderField(
                  "errorRate",
                  copy.errorRateLabel,
                  errorRate,
                  setErrorRateCustom,
                  {
                    kind: "percent",
                    range: HUBSPOT_ROI_SLIDER_RANGES.errorRate,
                  },
                )}
                <CcCalculatorCollapsible
                  id={`${ids}-assumptions`}
                  label={copy.assumptionsToggle}
                  open={assumptionsOpen}
                  onToggle={() => setAssumptionsOpen((open) => !open)}
                >
                  <p className="cc-traffic__help">{copy.assumptionsBody}</p>
                </CcCalculatorCollapsible>
              </>
            }
            results={
              <CcHubSpotRoiResultsPanel
                copy={copy}
                money={money}
                result={result}
                shareUrl={shareUrl}
                shareCopied={shareCopied}
                onCopyShareLink={onCopyShareLink}
              />
            }
          />
        </div>
      </div>
    </section>
  );
}
